import React from 'react';
import { Link } from 'react-router-dom';
import Tooltip from '@material-ui/core/Tooltip';
import { FormControl, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { css, StyleSheet } from 'aphrodite';

import { ApiHelper } from '../../common/helpers/ApiHelper';
import { Helper } from '../../common/helpers/Helper';
import { UiHelper } from '../../common/helpers/UiHelper';
import { BaseEntityPage } from '../../common/components/BaseEntityPage';
import { SearchBar } from '../../common/components/SearchBar';
import { AppConfig } from '../../AppConfig';
import { Controller } from '../../common/Controller';
import { AppStyles, AppTheme } from '../../styles/AppTheme';
import { CommonTheme } from '../../styles/Styles';

const queryString = require('query-string')

const ACCOUNT_FILTER_ALL = { key: 'all', label: 'All Zoho accounts' }

function getDeleteFormDef(current) {
  const email = (current && current.email) || ''
  return {
    title: email
      ? `${Helper.getString('zohoInboxesDeleteTitle')}: ${email}`
      : Helper.getString('zohoInboxesDeleteTitle'),
    description: Helper.getString('zohoInboxesDeleteWarn'),
    steps: [{ fields: [] }],
  }
}

function getDisableForwardingFormDef(current) {
  const email = (current && current.email) || ''
  return {
    title: email
      ? `${Helper.getString('zohoDisableForwardingTitle')}: ${email}`
      : Helper.getString('zohoDisableForwardingTitle'),
    description: Helper.getString('zohoDisableForwardingWarn'),
    ctaButtonText: Helper.getString('zohoDisableForwarding'),
    steps: [{ fields: [] }],
  }
}

function parseDomainFilter(search) {
  const qs = queryString.parse(search || '')
  const zoid = qs.zoid ? String(qs.zoid).trim() : ''
  const domain = qs.domain ? String(qs.domain).trim() : ''
  return {
    zoid,
    domain,
    isFilteredByDomain: !!(zoid && domain),
  }
}

export class ZohoInboxes extends BaseEntityPage {
  constructor(props) {
    super(props);
    this.ms = AppConfig.CDP_MS.ENDPOINT
    this.orgId = 'PRODUCT'
    this.pageTitle = Helper.getString('zohoInboxes')
    this.pageKey = 'zohoInboxes'
    this.breadCrumbs = [
      { title: Helper.getString('home'), to: Controller.get().getHomeByRole() },
      { title: Helper.getString('zohoDomains'), to: '/admin/zoho-domains' },
      { title: this.pageTitle },
    ]
    this.apiData = {}
    this.noAdd = true
    this.emptyText = Helper.getString('zohoInboxesEmpty')
    this.accounts = {}
    this.tableHeadersMap = {
      Email: (current) => current.email || '',
      Account: (current) => this.accountTitle(current.zoid),
      Name: (current) => {
        if (current.displayName) return current.displayName
        const first = current.firstName || ''
        const last = current.lastName || ''
        const name = `${first} ${last}`.trim()
        return name || '-'
      },
      Mailbox: (current) => current.mailboxAddress || current.primaryEmailAddress || '-',
      Status: (current) => current.status || '-',
      ZOID: (current) => current.zoid || '',
    }
    this.tableHeaders = Object.keys(this.tableHeadersMap)
    this.headerActions = [
      {
        label: 'Refresh',
        icon: 'refresh',
        onClick: () => this.fetchItems(),
      },
    ]
    this.applyQueryFilter(this.props.location && this.props.location.search)
  }

  applyQueryFilter(search) {
    const { zoid, domain, isFilteredByDomain } = parseDomainFilter(search)
    this.filterZoid = zoid
    this.filterDomain = domain
    this.isFilteredByDomain = isFilteredByDomain
    if (isFilteredByDomain) {
      this.toFetch = [
        {
          ms: this.ms,
          method: 'GET',
          paths: ['miscs', 'list-zoho-inboxes-by-domain'],
          queryParams: { zoid, domain },
        },
      ]
      this.emptyText = Helper.getString('zohoInboxesFilteredEmpty')
    } else {
      this.toFetch = [
        { ms: this.ms, method: 'GET', paths: ['miscs', 'list-zoho-inboxes'] },
      ]
      this.emptyText = Helper.getString('zohoInboxesEmpty')
    }
  }

  componentDidUpdate(prevProps) {
    const prevSearch = prevProps.location && prevProps.location.search
    const nextSearch = this.props.location && this.props.location.search
    if (prevSearch !== nextSearch) {
      this.applyQueryFilter(nextSearch)
      this.fetchItems()
    }
  }

  accountTitle(zoid) {
    const account = this.accounts && this.accounts[zoid]
    return (account && account.title) || zoid || ''
  }

  getAccountOptions() {
    const options = Object.keys(this.accounts || {}).map(zoid => ({
      key: zoid,
      label: this.accounts[zoid].title || zoid,
    })).sort((a, b) => a.label.localeCompare(b.label))
    return [ACCOUNT_FILTER_ALL, ...options]
  }

  getFilteredModeSubtitle() {
    if (!this.isFilteredByDomain) return ''
    const accountLabel = this.accountTitle(this.filterZoid)
    if (accountLabel && accountLabel !== this.filterZoid) {
      return `Inboxes for ${this.filterDomain} (${accountLabel})`
    }
    return `Inboxes for ${this.filterDomain} (zoid ${this.filterZoid})`
  }

  onFetchSuccess(results) {
    const data = results[0] || {}
    this.accounts = data.accounts || {}
    const items = (data.items || []).map((item, index) => ({
      ...item,
      id: item.email || `${item.zoid || 'zoid'}-${index}`,
      __index: index,
    }))
    this.setState({
      masterItemsList: items,
      items,
      searchText: '',
      accountFilterSelected: ACCOUNT_FILTER_ALL,
    })
  }

  onTableValue(current, index) {
    if (!this.tableHeaders[index]) { return '' }
    return this.tableHeadersMap[this.tableHeaders[index]](current)
  }

  openModalForDelete(current) {
    this.pendingAction = 'delete'
    this.actionItem = current
    this.fieldDef = this.formDefinition = getDeleteFormDef(current)
    this.apiData = {}
    super.openModalForDelete(current, this.formDefinition)
  }

  openModalForDisableForwarding(current) {
    this.pendingAction = 'disable_forwarding'
    this.actionItem = current
    this.fieldDef = this.formDefinition = getDisableForwardingFormDef(current)
    this.apiData = {}
    this.setState({
      apiMethod: 'POST',
      apiEndPoint: ApiHelper.makeUrlPath2({
        ms: this.ms,
        paths: ['miscs', 'update-zoho-inbox'],
      }),
    })
    super.openModalForEdit(current, this.formDefinition)
  }

  onDelete() {
    this.setState({
      apiMethod: 'POST',
      apiEndPoint: ApiHelper.makeUrlPath2({
        ms: this.ms,
        paths: ['miscs', 'delete-zoho-inboxes'],
      }),
    });
  }

  customSubmit(options) {
    const { formType, caller } = options
    const isDisableForwarding = this.pendingAction === 'disable_forwarding' && formType === 'edit'
    const isDelete = this.pendingAction === 'delete' && (formType === 'delete' || formType === 'del')

    if (!isDisableForwarding && !isDelete) {
      return caller.submit.call(caller, options.formData, true)
    }

    const item = this.actionItem || this.deletingItem || {}
    const endPoint = ApiHelper.makeUrlPath2({
      ms: this.ms,
      paths: ['miscs', isDisableForwarding ? 'update-zoho-inbox' : 'delete-zoho-inboxes'],
    })
    const jsonBody = isDisableForwarding
      ? {
        updateAction: 'disable_forwarding',
        email: item.email,
        zoid: item.zoid,
      }
      : {
        users: [{ email: item.email, zoid: item.zoid }],
      }

    ApiHelper.callAwait({
      method: 'POST',
      jsonBody,
      endPoint,
    }).then(() => {
      const successMsg = isDisableForwarding
        ? Helper.getString('zohoDisableForwardingSuccess')
        : Helper.getString('zohoInboxesDeleteSuccess')
      this.pendingAction = null
      this.actionItem = null
      this.deletingItem = null
      this.setState({
        modalOpen: false,
        showToast: true,
        isErrorToast: false,
        toastMsg: successMsg,
      })
      this.fetchItems()
    }).catch(err => {
      const errorMessage = Helper.getErrorMsg(err)
      caller.setState({
        fetchState: ApiHelper.State.ERROR,
        errMsg: errorMessage,
        success: false,
        message: errorMessage,
        inProcess: false,
      })
    })
  }

  onActions(current) {
    const actions = []
    if (current.forwarding === 'YES') {
      actions.push(
        <Tooltip title={Helper.getString('zohoDisableForwarding')} placement='top-start' key={(current.id || current.email) + '-disable-fwd'}>
          <span className={css(AppStyles.entityTableLinkIcon)}>
            <i className='material-icons-outlined' onClick={() => this.openModalForDisableForwarding(current)}>link_off</i>
          </span>
        </Tooltip>
      )
    }
    actions.push(UiHelper.buttonDelete(this, current))
    return actions
  }

  applyFilters(searchText = '', accountFilterSelected = ACCOUNT_FILTER_ALL) {
    const search = (searchText || '').toLowerCase().trim()
    const selected = accountFilterSelected || ACCOUNT_FILTER_ALL
    const zoidFilter = selected.key === 'all' ? '' : selected.key
    let items = [...(this.state.masterItemsList || [])]
    if (zoidFilter) {
      items = items.filter(item => String(item.zoid) === String(zoidFilter))
    }
    if (search) {
      items = items.filter(item => {
        const account = this.accountTitle(item.zoid).toLowerCase()
        const name = `${item.displayName || ''} ${item.firstName || ''} ${item.lastName || ''}`.toLowerCase()
        return (
          (item.email && item.email.toLowerCase().includes(search)) ||
          (item.zoid && String(item.zoid).toLowerCase().includes(search)) ||
          (item.mailboxAddress && item.mailboxAddress.toLowerCase().includes(search)) ||
          (item.primaryEmailAddress && item.primaryEmailAddress.toLowerCase().includes(search)) ||
          (item.status && String(item.status).toLowerCase().includes(search)) ||
          name.includes(search) ||
          account.includes(search)
        )
      })
    }
    this.setState({
      items,
      searchText,
      accountFilterSelected: selected,
    })
  }

  onSearch = (searchText = '') => {
    this.applyFilters(searchText, this.state.accountFilterSelected)
  }

  clearSearch = () => {
    this.applyFilters('', this.state.accountFilterSelected)
  }

  onAccountFilterChange = (event, val) => {
    this.applyFilters(this.state.searchText, val || ACCOUNT_FILTER_ALL)
  }

  renderAboveTable() {
    const accountOptions = this.getAccountOptions()
    const multiAccount = !this.isFilteredByDomain && accountOptions.length > 2
    const accountValue = this.state.accountFilterSelected || ACCOUNT_FILTER_ALL
    const filteredSubtitle = this.getFilteredModeSubtitle()

    return (
      <div className={css(Styles.toolbar)}>
        {this.isFilteredByDomain && (
          <div className={css(Styles.filteredBanner)}>
            <div className={css(Styles.filteredSubtitle)}>{filteredSubtitle}</div>
            <Link to='/admin/zoho-inboxes' className={css(Styles.clearFilterLink)}>
              {Helper.getString('zohoInboxesClearFilter')}
            </Link>
          </div>
        )}
        {multiAccount && (
          <div className={css(Styles.filterContainer)}>
            <div className={css(Styles.formControlContainer)}>
              <p className={css(Styles.label)}>Account</p>
              <FormControl className={css(Styles.formControlMainFilters)} variant='outlined'>
                <Autocomplete
                  value={accountValue}
                  options={accountOptions}
                  onChange={this.onAccountFilterChange}
                  getOptionLabel={option => option.label || option.key || ''}
                  getOptionSelected={(option, v) => option.key === v.key}
                  disableClearable
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant='standard'
                      className={css(Styles.formControlFilters)}
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        readOnly: true,
                        classes: {
                          root: css([Styles.textInputStyles, Styles.autoCompleteInputRoot]),
                          input: css(Styles.formInputStyle, Styles.textInput),
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
            </div>
          </div>
        )}
        <div className={css(Styles.searchBarContainer)} onSubmit={e => e.preventDefault()}>
          <SearchBar
            hint={Helper.getString('zohoInboxesSearchHint')}
            onSearch={this.onSearch}
            onClearSearch={this.clearSearch}
            searchText={this.state.searchText}
            searchButtonLabel='Filter'
            clearSearchLabel='Clear'
            autoRefresh
            debounceDuration={1}
            autoFocus={false}
            hideFiltersContainer
          />
        </div>
      </div>
    )
  }
}

const Styles = StyleSheet.create({
  toolbar: {
    width: '100%',
    marginBottom: 16,
  },
  filteredBanner: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 16,
  },
  filteredSubtitle: {
    fontSize: 14,
    color: CommonTheme.subTextColor,
    margin: 0,
  },
  clearFilterLink: {
    fontSize: 14,
    color: AppTheme.primaryColor,
    textDecoration: 'none',
    fontWeight: 500,
  },
  filterContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 16,
    overflowX: 'hidden',
    overflowY: 'visible',
    minWidth: 0,
  },
  formControlContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 5,
    flex: '1 1 220px',
    minWidth: 0,
    maxWidth: 320,
  },
  label: {
    fontSize: 14,
    color: CommonTheme.subTextColor,
    margin: 0,
    display: 'inline-block',
  },
  formControlMainFilters: {
    width: '100%',
  },
  formControlFilters: {
    borderRadius: AppTheme.borderRadius,
    border: '1px solid #E5E5E5',
    overflow: 'hidden',
  },
  textInputStyles: {
    padding: '11px 20px',
    flex: 1,
    textAlign: 'left',
    color: '#222222',
    fontSize: 16,
  },
  autoCompleteInputRoot: {
    paddingRight: 48,
  },
  formInputStyle: {
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  textInput: {
    padding: '4px 0px',
  },
  searchBarContainer: {
    width: '100%',
    minWidth: 0,
    overflowX: 'hidden',
  },
})
